import React, { useEffect, useMemo, useState } from 'react';
import { Box, Center, HStack, VStack } from '@chakra-ui/react';
import { TaskListHeader, TaskListProgressBar, TaskListFilterBar } from './TaskListHeader.tsx';
import { TaskList } from './TaskList.tsx';
import { TaskListSortBy, useGetCompanyOptions, useTaskListData } from '../hooks/TaskList.hooks.ts';
import { Loader } from 'Molecules/Loader/Loader.tsx';
import { EmptyState } from 'Atoms/EmptyState/EmptyState.tsx';
import { TaskToolbar } from 'Molecules/TaskToolbar/TaskToolbar.tsx';
import {
  GetDatapointsTasksDocument_,
  TaskStatusEnum_Enum_,
  useUpdateTasksMutation,
} from 'models/index.ts';
import { TaskFilterOptions } from 'containers/Esrs/pieces/DisclosureRequirements/Metrics/MetricsTable/MetricsTable.utils.tsx';
import { ContentLayout } from 'Molecules/index.ts';
import { useSearchParams } from 'react-router-dom';

const TaskListErrorState = () => {
  return (
    <EmptyState
      title="Something went wrong fetching the tasks"
      description="Press the button to attempt to refetch the task data"
      callToAction={{
        text: 'Retry',
        onClick: () => {
          window.location.reload();
        },
        variant: 'secondary',
      }}
      variant="gray"
    />
  );
};

export interface SelectedAssessmentOption {
  id: string;
  name: string;
  frameworkRef?: string;
  companyData: { id: string; name: string };
}

export const TaskManagementPage = () => {
  const [selectedAssessment, setSelectedAssessment] = useState<SelectedAssessmentOption>();
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [taskFilterOption, setTaskFilter] = useState(TaskFilterOptions.allTasks);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams] = useSearchParams();
  const urlTaskUserId = useMemo(() => searchParams.get('taskUserId'), [searchParams]);
  const [sortBy, setSortBy] = useState<TaskListSortBy>('deadline');
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [updateTasks] = useUpdateTasksMutation();

  const handleTaskUpdate = (selectedTaskIds: string[]) => {
    return (
      type: 'Responsible' | 'Deadline' | 'Status',
      data: {
        responsible?: string;
        deadline?: Date;
        status?: TaskStatusEnum_Enum_;
      }
    ) => {
      const setObject =
        type === 'Responsible'
          ? { ownerId: data.responsible }
          : type === 'Deadline'
            ? { dueDate: data.deadline }
            : { status: data.status };
      return updateTasks({
        variables: {
          tasksIds: selectedTaskIds,
          _set: {
            ...setObject,
          },
        },
        refetchQueries: [GetDatapointsTasksDocument_],
      }).then((res) => {
        return res;
      });
    };
  };

  const { companyOptions, loading: companyOptionsLoading } = useGetCompanyOptions();

  const {
    taskList: filteredTasks,
    userId,
    loading,
    error,
  } = useTaskListData({
    selectedAssessment,
    taskFilterOption,
    selectedMemberId,
    searchQuery,
    sortBy,
    sortDirection,
  });

  useEffect(() => {
    if (urlTaskUserId === userId) setTaskFilter(TaskFilterOptions.yourTasks);
  }, [urlTaskUserId]);

  const selectOptions = [
    {
      id: 'all',
      title: 'All tasks',
      onClick: () => setSelectedTaskIds(filteredTasks.map((task) => task.task?.id ?? '')),
    },
  ];

  // Progress counts
  const completed = filteredTasks
    .filter((row) => row.task)
    .reduce((acc, row) => acc + (row.task?.completedCount ?? 0), 0);

  const inProgress = filteredTasks
    .filter((row) => row.task)
    .reduce((acc, row) => acc + (row.task?.inProgressCount ?? 0), 0);

  const notStarted = filteredTasks
    .filter((row) => row.task)
    .reduce((acc, row) => acc + (row.task?.notStartedCount ?? 0), 0);

  const total = completed + inProgress + notStarted;

  const allSelectedTaskIds = useMemo(
    () =>
      filteredTasks
        .filter((task) => selectedTaskIds.includes(task?.task?.id ?? ''))
        .flatMap((t) => t.task?.taskIds)
        ?.filter((t): t is string => !!t),
    [filteredTasks, selectedTaskIds]
  );

  if (loading || companyOptionsLoading) {
    return <Loader isLoaded={!loading} />;
  } else if (error) {
    return <TaskListErrorState />;
  } else {
    return (
      <ContentLayout
        variant="narrow"
        header={<></>}
        relativeContent={
          !!selectedTaskIds.filter((id) => isNaN(Number(id))).length && (
            <TaskToolbar
              handleTaskUpdate={handleTaskUpdate(allSelectedTaskIds)}
              selectOptions={selectOptions}
              selectedText={`${selectedTaskIds.filter((id) => isNaN(Number(id))).length} task${selectedTaskIds.length > 1 ? 's' : ''} selected`}
              onClose={() => setSelectedTaskIds([])}
              hideStatus={true}
            />
          )
        }
      >
        <TaskListHeader />
        <VStack align="center" gap="4px">
          <TaskListFilterBar
            selectedAssessment={selectedAssessment}
            setSelectedAssessment={setSelectedAssessment}
            taskFilterOption={taskFilterOption}
            setTaskFilter={setTaskFilter}
            searchQuery={searchQuery}
            selectedUserId={selectedMemberId}
            setSelectedUserId={setSelectedMemberId}
            setSearchQuery={setSearchQuery}
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            completed={completed}
            total={total}
            companyOptions={companyOptions}
          />
          <TaskListProgressBar
            completed={completed}
            inProgress={inProgress}
            notStarted={notStarted}
            total={total}
          />
        </VStack>
        <Box ml="-28px">
          <TaskList
            taskList={filteredTasks}
            searchQuery={searchQuery}
            resetFilters={() => {
              setSearchQuery('');
              setSearchOpen(false);
              setTaskFilter(TaskFilterOptions.allTasks);
            }}
            selectedAssessment={selectedAssessment}
            selectedTasks={selectedTaskIds}
            setSelectedTasks={setSelectedTaskIds}
          />
        </Box>
      </ContentLayout>
    );
  }
};
